import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useCategoryStore } from 'stores/categoryStore';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import type { CommandPaletteDirectedSearchResult } from 'types/commandPaletteDirectedSearch';
import { searchCommandPaletteDirected } from 'utils/commandPaletteDirectedSearchRegistry';
import {
  getDirectedSearchAwaitingHint,
  parseCommandPaletteDirectedQuery,
} from 'utils/parseCommandPaletteDirectedQuery';

type DirectedSearchStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseCommandPaletteDirectedSearchResult {
  parsedQuery: ReturnType<typeof parseCommandPaletteDirectedQuery>;
  awaitingHint: ReturnType<typeof getDirectedSearchAwaitingHint>;
  results: CommandPaletteDirectedSearchResult[];
  status: DirectedSearchStatus;
}

function hasLoadedCategories(categories: Record<string, unknown>): boolean {
  return Object.keys(categories).length > 0;
}

export function useCommandPaletteDirectedSearch(
  query: string,
  isOpen: boolean,
  canAccessPath: (path: string) => boolean,
): UseCommandPaletteDirectedSearchResult {
  const activeCompanyId = useActiveCompanyStore(
    (state) => state.activeCompanyId,
  );
  const categories = useCategoryStore((state) => state.categories);
  const categoriesLoading = useCategoryStore((state) => state.loading);
  const reloadCategories = useCategoryStore((state) => state.reload);
  const parsedQuery = useMemo(
    () => parseCommandPaletteDirectedQuery(query),
    [query],
  );
  const awaitingHint = useMemo(
    () => getDirectedSearchAwaitingHint(query),
    [query],
  );
  const [results, setResults] = useState<CommandPaletteDirectedSearchResult[]>(
    [],
  );
  const [status, setStatus] = useState<DirectedSearchStatus>('idle');

  const categoriesReady = hasLoadedCategories(categories) || !categoriesLoading;

  const runSearch = useCallback(async () => {
    if (!parsedQuery || !activeCompanyId) {
      setResults([]);
      setStatus('idle');
      return;
    }

    if (parsedQuery.mode === 'general_ledger' && !categoriesReady) {
      setResults([]);
      setStatus('loading');
      return;
    }

    setStatus('loading');
    try {
      const nextResults = await searchCommandPaletteDirected(parsedQuery, {
        companyId: activeCompanyId,
        categories,
        canAccessPath,
      });
      setResults(nextResults);
      setStatus('success');
    } catch {
      setResults([]);
      setStatus('error');
    }
  }, [
    activeCompanyId,
    canAccessPath,
    categories,
    categoriesReady,
    parsedQuery,
  ]);

  const debouncedRunSearch = useDebouncedCallback(runSearch, 300);

  useEffect(() => {
    if (
      !isOpen ||
      !parsedQuery ||
      parsedQuery.mode !== 'general_ledger' ||
      hasLoadedCategories(categories) ||
      categoriesLoading
    ) {
      return;
    }

    void reloadCategories();
  }, [categories, categoriesLoading, isOpen, parsedQuery, reloadCategories]);

  useEffect(() => {
    if (!isOpen) {
      setResults([]);
      setStatus('idle');
      return;
    }

    debouncedRunSearch();
  }, [categoriesReady, debouncedRunSearch, isOpen, query]);

  return {
    parsedQuery,
    awaitingHint,
    results,
    status,
  };
}
